import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchSports, groupSportsByCategory, Sport } from "../utils/athleteApi";

type Props = {
    sportIds: number[];
    onChange: (sportIds: number[]) => void;
    label?: string;
    /** When true, assigned sports are shown as text instead of the category checklist. */
    readOnly?: boolean;
};

export const COACH_SPORTS_LIST_MAX_PX = 260;

export function placeFixedSportsList(trigger: DOMRect, viewportHeight: number): {
    openUpward: boolean;
    maxHeight: number;
    left: number;
    width: number;
    top?: number;
    bottom?: number;
} {
    const gap = 6;
    const spaceBelow = viewportHeight - trigger.bottom - gap;
    const spaceAbove = trigger.top - gap;
    const openUpward = spaceBelow < COACH_SPORTS_LIST_MAX_PX && spaceAbove > spaceBelow;
    const available = openUpward ? spaceAbove : spaceBelow;
    return {
        openUpward,
        maxHeight: Math.max(120, Math.min(COACH_SPORTS_LIST_MAX_PX, available)),
        left: trigger.left,
        width: trigger.width,
        ...(openUpward
            ? { bottom: viewportHeight - trigger.top + gap }
            : { top: trigger.bottom + gap }),
    };
}

export default function CoachSportsField({
    sportIds,
    onChange,
    label = "ASSIGN SPORT(S)",
    readOnly = false,
}: Props) {
    const [sports, setSports] = useState<Sport[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [listStyle, setListStyle] = useState<React.CSSProperties>({});
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const listRef = useRef<HTMLUListElement | null>(null);

    const updateListPlacement = useCallback(() => {
        const trigger = buttonRef.current?.getBoundingClientRect();
        if (!trigger) {
            return;
        }
        const placement = placeFixedSportsList(trigger, window.innerHeight);
        setListStyle({
            position: "fixed",
            left: placement.left,
            width: placement.width,
            maxHeight: placement.maxHeight,
            zIndex: 10050,
            ...(placement.openUpward
                ? { top: "auto", bottom: placement.bottom }
                : { top: placement.top, bottom: "auto" }),
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const catalog = await fetchSports();
            if (!cancelled) {
                setSports(Array.isArray(catalog) ? catalog : []);
                setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }
        updateListPlacement();
        const onDocClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (wrapRef.current?.contains(target) || listRef.current?.contains(target)) {
                return;
            }
            setOpen(false);
        };
        const onReposition = () => updateListPlacement();
        document.addEventListener("mousedown", onDocClick);
        window.addEventListener("resize", onReposition);
        window.addEventListener("scroll", onReposition, true);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            window.removeEventListener("resize", onReposition);
            window.removeEventListener("scroll", onReposition, true);
        };
    }, [open, updateListPlacement]);

    const selected = useMemo(
        () => sports.filter((sport) => sportIds.includes(sport.id)),
        [sports, sportIds]
    );
    const selectedLabel = selected.map((sport) => sport.name).join(", ");
    const groups = groupSportsByCategory(sports);

    const toggle = (id: number) => {
        if (readOnly) {
            return;
        }
        if (sportIds.includes(id)) {
            onChange(sportIds.filter((sportId) => sportId !== id));
        } else {
            onChange([...sportIds, id]);
        }
    };

    if (readOnly) {
        return (
            <div className="field coach-sports-field">
                <label>{label}</label>
                <div className="field-readonly">
                    <span className="readonly-value">
                        {loading ? "Loading sports…" : selectedLabel || "None assigned"}
                    </span>
                    <span className="readonly-note">
                        Assigned sports are set by a staff member or administrator
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="field coach-sports-field" ref={wrapRef}>
            <label>{label}</label>
            <button
                type="button"
                ref={buttonRef}
                className="coach-sports-button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={label}
                disabled={loading}
                onClick={() => setOpen((value) => !value)}
            >
                <span className={selectedLabel ? "coach-sports-value" : "coach-sports-placeholder"}>
                    {loading ? "Loading sports…" : selectedLabel || "Select sport(s)"}
                </span>
                <span className="coach-sports-caret" aria-hidden>▾</span>
            </button>
            {open &&
                createPortal(
                    <ul
                        ref={listRef}
                        className="coach-sports-list coach-sports-list--fixed"
                        role="listbox"
                        aria-multiselectable="true"
                        aria-label={label}
                        style={listStyle}
                    >
                        {groups.map((group) => (
                            <React.Fragment key={group.category}>
                                <li className="coach-sports-group" role="presentation">{group.category}</li>
                                {group.sports.map((sport) => {
                                    const checked = sportIds.includes(sport.id);
                                    return (
                                        <li key={sport.id}>
                                            <label className={`coach-sports-option${checked ? " is-selected" : ""}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggle(sport.id)}
                                                />
                                                <span>{sport.name}</span>
                                            </label>
                                        </li>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                        {sports.length === 0 && !loading && (
                            <li className="coach-sports-empty">No sports found</li>
                        )}
                    </ul>,
                    document.body
                )}
        </div>
    );
}
